import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { Truck, Wrench, Fuel, Activity, FileSpreadsheet, Send, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export function EquipmentModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'registry',
      title: 'Danh bạ Thiết bị',
      description: 'Quản lý hồ sơ lý lịch máy, khấu hao, thông số kỹ thuật và tình trạng.',
      icon: <Truck className="w-6 h-6" />,
      color: 'blue',
      path: '/equipment/assets',
      stats: '124 Thiết bị',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'maintenance',
      title: 'Bảo dưỡng & Sửa chữa',
      description: 'Lập lịch bảo dưỡng định kỳ, ghi nhận nhật ký sửa chữa và thay thế phụ tùng.',
      icon: <Wrench className="w-6 h-6" />,
      color: 'amber',
      path: '/equipment/maintenance',
      stats: '8 Đang bảo dưỡng',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'fuel',
      title: 'Nhật ký Nhiên liệu',
      description: 'Cấp phát xăng/dầu, theo dõi định mức tiêu hao và cảnh báo rò rỉ.',
      icon: <Fuel className="w-6 h-6" />,
      color: 'indigo',
      path: '/equipment/fuel',
      stats: 'Định mức: 12L/h',
      gradient: 'from-indigo-500 to-violet-500',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    },
    {
      id: 'usage',
      title: 'Theo dõi Vận hành',
      description: 'Ghi nhận giờ máy hoạt động (Ca làm việc) và phân bổ chi phí cho dự án.',
      icon: <Activity className="w-6 h-6" />,
      color: 'emerald',
      path: '/equipment/usage',
      stats: 'Đang chạy: 45',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'requests',
      title: 'Yêu cầu Cấp phát',
      description: 'Tiếp nhận, phê duyệt và điều động máy móc từ các công trường.',
      icon: <Send className="w-6 h-6" />,
      color: 'purple',
      path: '/equipment/requests',
      stats: '5 Yêu cầu mới',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'costs',
      title: 'Phân tích Chi phí',
      description: 'Báo cáo tổng chi phí vận hành (TCO), tỷ suất lợi nhuận trên từng đầu máy.',
      icon: <FileSpreadsheet className="w-6 h-6" />,
      color: 'slate',
      path: '/equipment/costs',
      stats: 'Báo cáo tháng',
      gradient: 'from-slate-500 to-gray-600',
      bgLight: 'bg-slate-100',
      textDark: 'text-slate-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-blue-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-blue-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
